import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { CrudTotalSchemaItem } from "@/shared/components/CRUD/CrudTotal";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import type { RevenueShareReportsListRow } from "@/admin/entities/revenueShareReports";
import type { RevenueShareReportsListQueryType } from "@/types/graphql/admin.generated";
import {
  buildFeeColumn,
  buildPeriodColumn,
  buildProviderFeeColumn,
  buildSalesColumn,
  buildStatusColumn,
  buildTotalColumn,
} from "../domains/revenueShareReportColumns";
import { formatMoney } from "../domains/billingFormat";
import { renderRevenueShareReportStatus } from "../domains/billingLabels";

export function getTableData(data: unknown): {
  nodes: RevenueShareReportsListRow[];
  count: number;
} {
  const typed = data as RevenueShareReportsListQueryType;
  const result = typed?.revenueShareReports;
  if (!result?.nodes) return { nodes: [], count: 0 };
  return { nodes: result.nodes, count: result.count };
}

export function mapColumns(report: RevenueShareReportsListRow) {
  return [
    report.id,
    report,
    report.period,
    report.status,
    report,
    report,
    report,
    report,
  ];
}

export const getColumnsConfig =
  (): ColumnDefinition<RevenueShareReportsListRow>[] => [
    { name: "id", label: "ID", options: { display: false, filter: false } },
    {
      name: "organization",
      label: "Организация",
      options: {
        sort: false,
        customBodyRender: (data: unknown) => {
          const report = data as RevenueShareReportsListRow;
          return (
            <CrudLink
              path={PagePaths.organizations}
              tab="revenueShareReports"
              id={report.organizationId}
            >
              {report.organization?.title ?? ""}
            </CrudLink>
          );
        },
      },
    },
    buildPeriodColumn(),
    buildStatusColumn(),
    buildSalesColumn(),
    buildProviderFeeColumn(),
    buildFeeColumn(),
    buildTotalColumn(),
  ];

/** Итоги по текущей странице, см. тот же приём в paymentsConfig. */
export const getTotalSchema = (): CrudTotalSchemaItem[] => [
  {
    xs: 12,
    md: 4,
    label: "Отчётов на странице",
    value: (data: unknown) => getTableData(data).nodes.length,
  },
  {
    xs: 12,
    md: 4,
    label: "К оплате на странице",
    value: (data: unknown) =>
      getTableData(data).nodes.filter((report) => report.status === "payable")
        .length,
  },
  {
    xs: 12,
    md: 4,
    label: "Итог на странице",
    value: (data: unknown) => {
      const nodes = getTableData(data).nodes;
      const byCurrency = new Map<string, number>();
      for (const report of nodes) {
        const amount =
          report.reportAmountInContractCurrency ?? report.reportAmount;
        byCurrency.set(
          amount.currency,
          (byCurrency.get(amount.currency) ?? 0) + amount.value
        );
      }
      return [...byCurrency.entries()]
        .map(([currency, value]) => formatMoney({ value, currency }))
        .join(", ");
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Отчёты доли с продаж",
  refetchList: "Обновить список",
  cancel: "Отмена",
  editItem: "Отчёт",
  showTitle: "Детали",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<RevenueShareReportsListRow> => ({
    main: {
      title: "Отчёт",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap>{row.period}</Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.organization?.title ?? ""}
          </Typography>
        </Stack>
      ),
    },
    status: {
      title: "Статус",
      renderCell: (row) => renderRevenueShareReportStatus(row.status),
    },
  });
